/**
 * useAnalysisStatus Hook
 *
 * Tracks the status of feedback and insights generation stages
 * after speaker confirmation. Uses SSE pipeline events to provide
 * real-time updates for inline processing states in the Summary tab.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { usePipelineEvents } from "./usePipelineEvents";
import type { StageStatus, PipelineError } from "@/types/pipeline";

/**
 * Status for a single analysis component
 */
export interface AnalysisComponentStatus {
  /** Whether this component is currently generating */
  isGenerating: boolean;
  /** Whether generation has completed successfully */
  isComplete: boolean;
  /** Whether generation failed */
  isFailed: boolean;
  /** Progress percentage (0-100) */
  progress: number;
  /** Error details if failed */
  error?: PipelineError;
}

/**
 * Return type for useAnalysisStatus hook
 */
export interface UseAnalysisStatusResult {
  /** Status for the Conversation Summary section (requires insights stage) */
  summaryStatus: AnalysisComponentStatus;
  /** Status for the Therapist Analysis section (requires feedback stage) */
  analysisStatus: AnalysisComponentStatus;
  /** Status for the Conversation Insights sidebar (requires insights stage) */
  insightsStatus: AnalysisComponentStatus;
  /** Overall status - true if any component is still generating */
  isAnyGenerating: boolean;
  /** Overall status - true if all completed (or no generation was needed) */
  isAllComplete: boolean;
  /** Pipeline connection status */
  isConnected: boolean;
  /** Manually refetch data (for retry after failure) */
  retry: () => void;
}

/**
 * Options for the useAnalysisStatus hook
 */
export interface UseAnalysisStatusOptions {
  /** Reflection ID to monitor */
  reflectionId: string | undefined;
  /** Whether the reflection is completed (triggers hook) */
  isCompleted: boolean;
  /** Whether speakers have been confirmed (analysis starts after this) */
  speakersConfirmed: boolean;
  /** Access token function for SSE auth */
  getAccessToken?: () => Promise<string>;
  /** Callback when all analysis completes */
  onComplete?: () => void;
  /** Callback when analysis fails */
  onError?: (error: PipelineError) => void;
}

/**
 * Create a default "complete" status for when no generation is needed
 */
function createCompleteStatus(): AnalysisComponentStatus {
  return {
    isGenerating: false,
    isComplete: true,
    isFailed: false,
    progress: 100,
  };
}

/**
 * Create a default "pending" status for when generation hasn't started
 */
function createPendingStatus(): AnalysisComponentStatus {
  return {
    isGenerating: false,
    isComplete: false,
    isFailed: false,
    progress: 0,
  };
}

/**
 * Hook to track analysis generation status for Summary tab components.
 *
 * This hook subscribes to pipeline SSE events and extracts the status
 * of the feedback and insights stages. It provides granular status for
 * each UI component:
 *
 * - Summary: Depends on insights stage (annotation summary)
 * - Analysis: Depends on feedback stage (therapist feedback)
 * - Insights: Depends on insights stage (individual annotations)
 *
 * @example
 * ```tsx
 * const { summaryStatus, analysisStatus, insightsStatus } = useAnalysisStatus({
 *   reflectionId,
 *   isCompleted: true,
 *   speakersConfirmed: true,
 *   getAccessToken,
 * });
 *
 * if (summaryStatus.isGenerating) {
 *   return <GeneratingState message="Creating conversation summary..." />;
 * }
 * ```
 */
export function useAnalysisStatus(
  options: UseAnalysisStatusOptions
): UseAnalysisStatusResult {
  const {
    reflectionId,
    isCompleted,
    speakersConfirmed,
    getAccessToken,
    onComplete,
    onError,
  } = options;

  // Track whether we've seen the stages complete to avoid re-enabling SSE
  const [hasSeenComplete, setHasSeenComplete] = useState(false);

  // Track retry trigger to force reconnection
  const [retryCount, setRetryCount] = useState(0);

  // Determine if we should enable SSE monitoring
  // Enable when: reflection exists, speakers confirmed, not already seen complete
  const shouldMonitor =
    !!reflectionId && isCompleted && speakersConfirmed && !hasSeenComplete;

  // Subscribe to pipeline events
  const { status, isConnected, reconnect } = usePipelineEvents({
    reflectionId: reflectionId || "",
    enabled: shouldMonitor,
    getAccessToken,
    onComplete: () => {
      setHasSeenComplete(true);
      onComplete?.();
    },
    onError,
  });

  // Retry handler - reconnects SSE and resets seen state
  const retry = useCallback(() => {
    setHasSeenComplete(false);
    setRetryCount((c) => c + 1);
    reconnect();
  }, [reconnect]);

  // Re-trigger monitoring on retry
  useEffect(() => {
    if (retryCount > 0 && shouldMonitor) {
      reconnect();
    }
  }, [retryCount, shouldMonitor, reconnect]);

  // Extract status for each stage
  const feedbackStageInfo = status?.stages?.feedback;
  const insightsStageInfo = status?.stages?.insights;

  // CRITICAL: If we've seen completion, return complete status regardless of stage info
  // This prevents stuck loading states when SSE events arrive out of order
  // or when the connection drops before all events are processed
  const pipelineIsComplete =
    hasSeenComplete || status?.current_status === "completed";

  // Derive component statuses from pipeline stages
  const summaryStatus = useMemo((): AnalysisComponentStatus => {
    // If pipeline completed, always return complete
    if (pipelineIsComplete) {
      return createCompleteStatus();
    }

    // Summary depends on insights stage (annotation summary)
    if (!insightsStageInfo) {
      // No status yet - if speakers not confirmed, we're done (no generation needed)
      // If speakers are confirmed, we're waiting for SSE connection
      return speakersConfirmed ? createPendingStatus() : createCompleteStatus();
    }

    const stageStatus = insightsStageInfo.status as StageStatus;

    return {
      isGenerating: stageStatus === "in_progress" || stageStatus === "pending",
      isComplete: stageStatus === "completed" || stageStatus === "skipped",
      isFailed: stageStatus === "failed",
      progress: insightsStageInfo.progress || 0,
      error: insightsStageInfo.error,
    };
  }, [insightsStageInfo, speakersConfirmed, pipelineIsComplete]);

  const analysisStatus = useMemo((): AnalysisComponentStatus => {
    // If pipeline completed, always return complete
    if (pipelineIsComplete) {
      return createCompleteStatus();
    }

    // Analysis depends on feedback stage
    if (!feedbackStageInfo) {
      return speakersConfirmed ? createPendingStatus() : createCompleteStatus();
    }

    const stageStatus = feedbackStageInfo.status as StageStatus;

    return {
      isGenerating: stageStatus === "in_progress" || stageStatus === "pending",
      isComplete: stageStatus === "completed" || stageStatus === "skipped",
      isFailed: stageStatus === "failed",
      progress: feedbackStageInfo.progress || 0,
      error: feedbackStageInfo.error,
    };
  }, [feedbackStageInfo, speakersConfirmed, pipelineIsComplete]);

  const insightsStatus = useMemo((): AnalysisComponentStatus => {
    // If pipeline completed, always return complete
    if (pipelineIsComplete) {
      return createCompleteStatus();
    }

    // Insights sidebar also depends on insights stage
    if (!insightsStageInfo) {
      return speakersConfirmed ? createPendingStatus() : createCompleteStatus();
    }

    const stageStatus = insightsStageInfo.status as StageStatus;

    return {
      isGenerating: stageStatus === "in_progress" || stageStatus === "pending",
      isComplete: stageStatus === "completed" || stageStatus === "skipped",
      isFailed: stageStatus === "failed",
      progress: insightsStageInfo.progress || 0,
      error: insightsStageInfo.error,
    };
  }, [insightsStageInfo, speakersConfirmed, pipelineIsComplete]);

  // Aggregate statuses
  const isAnyGenerating =
    summaryStatus.isGenerating ||
    analysisStatus.isGenerating ||
    insightsStatus.isGenerating;

  const isAllComplete =
    summaryStatus.isComplete &&
    analysisStatus.isComplete &&
    insightsStatus.isComplete;

  return {
    summaryStatus,
    analysisStatus,
    insightsStatus,
    isAnyGenerating,
    isAllComplete,
    isConnected,
    retry,
  };
}
